import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Icon,
  HStack,
  Badge,
  Grid,
  Divider,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Card,
  Spinner,
  useColorModeValue,
  useToken,
  Skeleton,
} from '@chakra-ui/react'
import { keyframes } from '@emotion/react'
import { Link as RouterLink } from 'react-router-dom'
import { MapPin, MagnifyingGlass } from '@phosphor-icons/react'
import { useStats } from '../../hooks/useStats'
import { useRandomSites } from '../../hooks/useRandomSites'
import { getLocalStorage, setLocalStorage } from '../../utils/storage'
import { useEnhancedTexture } from '../../context/EnhancedTextureContext'
import SiteCarousel from '../SiteCarousel'

// Constants for local storage
const BACKGROUND_LOADED_KEY = 'hero_bg_loaded';

// Optimized animations with reduced motion support
const fadeInUp = keyframes`
  0% { 
    opacity: 0; 
    transform: translateY(20px);
  }
  100% { 
    opacity: 1; 
    transform: translateY(0);
  }
`;

const slideInFromLeft = keyframes`
  0% { 
    opacity: 0; 
    transform: translateX(-30px);
  }
  100% { 
    opacity: 1; 
    transform: translateX(0);
  }
`;

// Performance-optimized image preloader
const useImagePreloader = (images) => {
  const [loaded, setLoaded] = useState(() => getLocalStorage(BACKGROUND_LOADED_KEY) || false);
  
  useEffect(() => {
    if (loaded) return;
    
    let loadedCount = 0;
    const totalImages = images.length;
    
    const preloadImage = (src) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve; // Continue even if image fails
        img.src = src;
      });
    };
    
    Promise.all(images.map(preloadImage)).then(() => {
      setLoaded(true);
      setLocalStorage(BACKGROUND_LOADED_KEY, true);
    });
  }, [images, loaded]);
  
  return loaded;
};

function OptimizedHeroSection() {
  const { getFullBackgroundStyles, isTextureEnabled } = useEnhancedTexture();
  const colorMode = useColorModeValue('light', 'dark');
  
  // Optimized background image loading
  const backgroundImages = [
    '/textures/homepage/worldbglight-new.png',
    '/textures/homepage/worldbgdark.png'
  ];
  const bgLoaded = useImagePreloader(backgroundImages);

  const [borderLight, borderDefault, borderHeavy, bgSurfaceLight, bgSurfaceDark] = useToken(
    'colors',
    ['border.light', 'border.default', 'border.heavy', 'bg.surface', 'bg.surface._dark']
  );

  const stats = useStats();
  const { data: randomSites } = useRandomSites();

  // Current date formatted as handwritten journal entry
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Get texture styles for different components
  const heroContainerTexture = getFullBackgroundStyles('linen', colorMode);
  const carouselTexture = getFullBackgroundStyles('leather', colorMode, { zIndex: 0 });

  return (
    <Box
      bg={useColorModeValue('gray.50', 'gray.900')}
      py={{ base: 6, md: 16, lg: 20 }}
      position="relative"
      overflow="hidden"
      borderBottom="1px solid"
      borderColor="border.default"
      transition="background-color 0.2s ease-in-out"
      minH={{ base: 'auto', md: '100vh' }}
      // Full component background texture
      sx={isTextureEnabled ? {
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgImage: useColorModeValue(
            'url("/textures/homepage/worldbglight-new.png")', 
            'url("/textures/homepage/worldbgdark.png")'
          ),
          bgRepeat: 'repeat',
          bgSize: { base: '150%', md: '100%' },
          bgPosition: 'center center',
          opacity: bgLoaded ? 0.8 : 0,
          transition: 'opacity 0.5s ease-in-out',
          pointerEvents: 'none',
          zIndex: 0
        }
      } : {}}
    >
      <Container
        variant="journal"
        maxW={{ base: "100%", md: "container.xl" }}
        position="relative"
        zIndex={1}
        mx="auto"
        px={{ base: 4, md: 6, lg: 8 }}
        py={{ base: 6, md: 8, lg: 12 }}
        width="auto"
        bg={useColorModeValue("rgba(255, 255, 255, 0.7)", "rgba(40, 35, 30, 0.8)")}
        boxShadow={{ base: "md", md: "xl" }}
        borderRadius={{ base: "lg", md: "xl" }}
        overflow="hidden"
        // Full background texture for container
        sx={heroContainerTexture}
      >
        {/* Date Stamp - Mobile optimized */}
        <Box
          position="absolute"
          top={{ base: "8px", md: "15px", lg: "20px" }}
          right={{ base: "8px", md: "20px", lg: "25px" }}
          transform={{ base: "rotate(3deg)", lg: "rotate(8deg)" }}
          fontFamily="handwritten"
          fontSize={{ base: "2xs", md: "xs", lg: "sm" }}
          color="text.muted"
          zIndex={2}
          display="block"
          pointerEvents="none"
          opacity={{ base: 0.8, md: 1 }}
          _after={{
            content: `"${currentDate}"`,
            display: 'block',
            p: { base: 1, md: 1.5, lg: 2 },
            bg: useColorModeValue('rgba(255, 255, 255, 0.8)', 'rgba(0, 0, 0, 0.6)'),
            borderRadius: "sm",
            border: '1px dashed',
            borderColor: borderLight,
            boxShadow: "sm",
          }}
        />

        {/* Mobile layout (text first, then carousel, then stats) */}
        <Box display={{ base: 'block', lg: 'none' }} width="100%" mb={6}>
          {/* Mobile text content first */}
          <VStack
            spacing={{ base: 5, lg: 8 }}
            textAlign="center"
            alignItems="center"
            opacity={0.9}
            width="100%"
            mb={8}
            sx={{
              '@media(prefers-reduced-motion: no-preference)': {
                '& > *': {
                  opacity: 0,
                  transform: 'translateY(10px)',
                  animation: `${fadeInUp} 0.8s forwards`,
                  '&:nth-of-type(1)': { animationDelay: '0.1s' },
                  '&:nth-of-type(2)': { animationDelay: '0.3s' },
                  '&:nth-of-type(3)': { animationDelay: '0.5s' },
                }
              }
            }}
          >
            <VStack spacing={4} alignItems="center">
              <Badge variant="journal">Megalithic Research Platform</Badge>
              <Heading
                fontSize="3xl"
                color="text.default"
                lineHeight="1.1"
                fontFamily="heading"
                position="relative"
                _after={{
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60px',
                  height: '3px',
                  bgGradient: 'linear(to-r, interactive.primary, border.light)',
                  borderRadius: 'full',
                }}
              >
                Explore Megalithic Sites
              </Heading>
              <Text 
                fontSize="md"
                maxW="2xl" 
                color="text.muted"
                px={2}
              >
                Join a global network of researchers, archaeologists, and scientists working together to document, preserve, and understand our megalithic heritage through standardized methodology and cutting-edge tools.
              </Text>
              <Flex 
                direction={{ base: 'column', sm: 'row' }}
                gap={{ base: 3, sm: 4 }}
                pt={2}
                width="100%"
              >
                <Button
                  as={RouterLink}
                  to="/map"
                  variant="map"
                  size="md"
                  leftIcon={<Icon as={MapPin} weight="bold" />}
                  bg="map.land"
                  borderWidth="1.5px"
                  width="100%"
                  _hover={{
                    transform: 'translateY(-1px)',
                    boxShadow: 'sm',
                    borderColor: 'map.markers',
                  }}
                  _active={{
                    transform: 'translateY(0px)',
                  }}
                >
                  Explore Map
                </Button>
                <Button
                  as={RouterLink}
                  to="/search"
                  variant="map"
                  size="md"
                  leftIcon={<Icon as={MagnifyingGlass} weight="bold" />}
                  bg="map.land"
                  borderWidth="1.5px"
                  width="100%"
                  _hover={{
                    transform: 'translateY(-1px)',
                    boxShadow: 'sm',
                    borderColor: 'map.markers',
                  }}
                  _active={{
                    transform: 'translateY(0px)',
                  }}
                >
                  Search Sites
                </Button>
              </Flex>
            </VStack>
          </VStack>
          
          {/* Mobile Carousel AFTER intro text */}
          <Box
            position="relative"
            borderWidth={{ base: "3px" }}
            borderColor="border.heavy"
            borderRadius="md"
            boxShadow="md"
            backgroundColor="bg.surface"
            overflow="hidden"
            height={{ base: "300px" }}
            mb={6}
            sx={carouselTexture}
          >
            <Box position="relative" zIndex={2} height="100%">
              <SiteCarousel sites={randomSites} />
            </Box>
          </Box>
        </Box>

        {/* Desktop layout with grid - LEFT: text/stats, RIGHT: carousel */}
        <Grid
          templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
          gap={{ base: 6, lg: 10 }}
          alignItems="center"
          mb={{ base: 6, lg: 10 }}
          position="relative"
          zIndex={1}
          display={{ base: 'none', lg: 'grid' }}
        >
          {/* LEFT COLUMN: Text and Stats */}
          <VStack
            spacing={{ base: 5, lg: 8 }}
            textAlign={{ base: 'center', lg: 'left' }}
            alignItems={{ base: 'center', lg: 'flex-start' }}
            opacity={0.9}
            w="100%"
            sx={{
              '@media(prefers-reduced-motion: no-preference)': {
                '& > *': {
                  opacity: 0,
                  transform: 'translateY(10px)',
                  animation: `${fadeInUp} 0.8s forwards`,
                  '&:nth-of-type(1)': { animationDelay: '0.1s' },
                  '&:nth-of-type(2)': { animationDelay: '0.3s' },
                  '&:nth-of-type(3)': { animationDelay: '0.5s' },
                }
              }
            }}
          >
            <VStack spacing={{ base: 4, lg: 6 }} alignItems={{ base: 'center', lg: 'flex-start' }}>
              <Badge variant="journal">Megalithic Research Platform</Badge>
              <Heading
                fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
                color="text.default"
                lineHeight="1.1"
                fontFamily="heading"
                position="relative"
                _after={{
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: { base: '50%', lg: '0' },
                  transform: { base: 'translateX(-50%)', lg: 'translateX(0)' },
                  width: { base: '60px', md: '100px' },
                  height: '3px',
                  bgGradient: 'linear(to-r, interactive.primary, border.light)',
                  borderRadius: 'full',
                }}
              >
                Explore Megalithic Sites
              </Heading>
              <Text 
                fontSize={{ base: 'md', md: 'xl' }}
                maxW="2xl" 
                color="text.muted"
                px={{ base: 2, lg: 0 }}
              >
                Join a global network of researchers, archaeologists, and scientists working together to document, preserve, and understand our megalithic heritage.
              </Text>
              <Flex 
                direction={{ base: 'column', sm: 'row' }}
                gap={{ base: 3, sm: 4 }}
                pt={2}
                width={{ base: '100%', sm: 'auto' }}
              >
                <Button
                  as={RouterLink}
                  to="/map"
                  variant="map"
                  size={{ base: 'md', lg: 'lg' }}
                  leftIcon={<Icon as={MapPin} weight="bold" />}
                  bg="map.land"
                  borderWidth="1.5px"
                  width={{ base: '100%', sm: 'auto' }}
                  _hover={{
                    transform: 'translateY(-1px)',
                    boxShadow: 'sm',
                    borderColor: 'map.markers',
                  }}
                  _active={{
                    transform: 'translateY(0px)',
                  }}
                >
                  Explore Map
                </Button>
                <Button
                  as={RouterLink}
                  to="/search"
                  variant="map"
                  size={{ base: 'md', lg: 'lg' }}
                  leftIcon={<Icon as={MagnifyingGlass} weight="bold" />}
                  bg="map.land"
                  borderWidth="1.5px"
                  width={{ base: '100%', sm: 'auto' }}
                  _hover={{
                    transform: 'translateY(-1px)',
                    boxShadow: 'sm',
                    borderColor: 'map.markers',
                  }}
                  _active={{
                    transform: 'translateY(0px)',
                  }}
                >
                  Search Sites
                </Button>
              </Flex>

              {/* Desktop Stats Section */}
              <Flex
                direction={{ base: 'column', md: 'row' }}
                gap={{ base: 4, md: 6 }}
                py={{ base: 3, md: 4 }}
                px={{ base: 2, md: 4 }}
                mt={{ base: 0, md: 8 }}
                borderTop="1px solid"
                borderBottom="1px solid"
                borderColor="border.light"
                width="full"
                justifyContent="space-around"
                alignItems="center"
              >
                {stats.loading ? (
                  <Flex gap={6} justify="space-around" width="100%">
                    <Skeleton height="60px" width="100px" />
                    <Skeleton height="60px" width="100px" />
                    <Skeleton height="60px" width="100px" />
                  </Flex>
                ) : (
                  <>
                    {/* Stat 1 */}
                    <VStack 
                      spacing={0} 
                      textAlign="center"
                      borderRadius="md"
                      py={{ base: 2, md: 0 }}
                      width={{ base: "full", md: "auto" }}
                      borderBottom={{ base: "1px solid", md: "none" }}
                      borderColor={{ base: "border.light", md: "transparent" }}
                    >
                      <Stat>
                        <StatLabel
                          fontSize={{ base: "2xs", md: "xs" }}
                          textTransform="uppercase"
                          color="text.muted"
                          fontWeight="medium"
                          mb={0}
                        >
                          Sites Documented
                        </StatLabel>
                        <StatNumber
                          fontSize={{ base: "xl", md: "2xl" }}
                          fontWeight="medium"
                          color="text.default"
                          lineHeight="1.2"
                        >
                          {stats.sitesCount.toLocaleString()}+
                        </StatNumber>
                      </Stat>
                    </VStack>

                    <Divider
                      orientation="vertical"
                      height="40px"
                      borderColor="border.light"
                      display={{ base: 'none', md: 'block' }}
                    />

                    {/* Stat 2 */}
                    <VStack 
                      spacing={0} 
                      textAlign="center"
                      borderRadius="md"
                      py={{ base: 2, md: 0 }}
                      width={{ base: "full", md: "auto" }}
                      borderBottom={{ base: "1px solid", md: "none" }}
                      borderColor={{ base: "border.light", md: "transparent" }}
                    >
                      <Stat>
                        <StatLabel
                          fontSize={{ base: "2xs", md: "xs" }}
                          textTransform="uppercase"
                          color="text.muted"
                          fontWeight="medium"
                          mb={0}
                        >
                          Active Researchers
                        </StatLabel>
                        <StatNumber
                          fontSize={{ base: "xl", md: "2xl" }}
                          fontWeight="medium"
                          color="text.default"
                          lineHeight="1.2"
                        >
                          {stats.researchersCount.toLocaleString()}+
                        </StatNumber>
                      </Stat>
                    </VStack>

                    <Divider
                      orientation="vertical"
                      height="40px"
                      borderColor="border.light"
                      display={{ base: 'none', md: 'block' }}
                    />

                    {/* Stat 3 */}
                    <VStack 
                      spacing={0} 
                      textAlign="center"
                      borderRadius="md"
                      py={{ base: 2, md: 0 }}
                      width={{ base: "full", md: "auto" }}
                    >
                      <Stat>
                        <StatLabel
                          fontSize={{ base: "2xs", md: "xs" }}
                          textTransform="uppercase"
                          color="text.muted"
                          fontWeight="medium"
                          mb={0}
                        >
                          Media Uploads
                        </StatLabel>
                        <StatNumber
                          fontSize={{ base: "xl", md: "2xl" }}
                          fontWeight="medium"
                          color="text.default"
                          lineHeight="1.2"
                        >
                          {stats.mediaCount.toLocaleString()}+
                        </StatNumber>
                      </Stat>
                    </VStack>
                  </>
                )}
              </Flex>
            </VStack>
          </VStack>

          {/* RIGHT COLUMN: Carousel Box */}
          <Box
            position="relative"
            borderWidth="4px"
            borderColor="border.heavy"
            borderRadius="md"
            boxShadow="md"
            backgroundColor="bg.surface"
            overflow="hidden"
            height="400px"
            sx={carouselTexture}
          >
            <Box position="relative" zIndex={2} height="100%">
              <SiteCarousel sites={randomSites} />
            </Box>
          </Box>
        </Grid>

        {/* Mobile Stats Section (below carousel) */}
        <Flex
          direction="column"
          gap={4}
          py={3}
          px={2}
          mt={2}
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor="border.light"
          width="full"
          justifyContent="space-around"
          alignItems="center"
          display={{ base: 'flex', lg: 'none' }}
        >
          {stats.loading ? (
            <Flex gap={6} justify="space-around" width="100%">
              <Skeleton height="60px" width="100px" />
              <Skeleton height="60px" width="100px" />
              <Skeleton height="60px" width="100px" />
            </Flex>
          ) : (
            <>
              {/* Mobile Stat 1 */}
              <VStack 
                spacing={0} 
                textAlign="center"
                borderRadius="md"
                py={2}
                width="full"
                borderBottom="1px solid"
                borderColor="border.light"
              >
                <Stat>
                  <StatLabel
                    fontSize="2xs"
                    textTransform="uppercase"
                    color="text.muted"
                    fontWeight="medium"
                    mb={0}
                  >
                    Sites Documented
                  </StatLabel>
                  <StatNumber
                    fontSize="xl"
                    fontWeight="medium"
                    color="text.default"
                    lineHeight="1.2"
                  >
                    {stats.sitesCount.toLocaleString()}+
                  </StatNumber>
                </Stat>
              </VStack>

              {/* Mobile Stat 2 */}
              <VStack 
                spacing={0} 
                textAlign="center"
                borderRadius="md"
                py={2}
                width="full"
                borderBottom="1px solid"
                borderColor="border.light"
              >
                <Stat>
                  <StatLabel
                    fontSize="2xs"
                    textTransform="uppercase"
                    color="text.muted"
                    fontWeight="medium"
                    mb={0}
                  >
                    Active Researchers
                  </StatLabel>
                  <StatNumber
                    fontSize="xl"
                    fontWeight="medium"
                    color="text.default"
                    lineHeight="1.2"
                  >
                    {stats.researchersCount.toLocaleString()}+
                  </StatNumber>
                </Stat>
              </VStack>

              {/* Mobile Stat 3 */}
              <VStack 
                spacing={0} 
                textAlign="center"
                borderRadius="md"
                py={2}
                width="full"
              >
                <Stat>
                  <StatLabel
                    fontSize="2xs"
                    textTransform="uppercase"
                    color="text.muted"
                    fontWeight="medium"
                    mb={0}
                  >
                    Media Uploads
                  </StatLabel>
                  <StatNumber
                    fontSize="xl"
                    fontWeight="medium"
                    color="text.default"
                    lineHeight="1.2"
                  >
                    {stats.mediaCount.toLocaleString()}+
                  </StatNumber>
                </Stat>
              </VStack>
            </>
          )}
        </Flex>
      </Container>
    </Box>
  );
}

export { OptimizedHeroSection };
